import React, { useCallback, useEffect, useState } from 'react';
import { FiRefreshCw, FiAlertTriangle, FiRepeat, FiX, FiPlus, FiTrash2, FiPlay } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { financeAPI } from '../../services/api';
import * as fs from './financeStyles';

const FREQUENCIES = ['WEEKLY', 'MONTHLY', 'QUARTERLY', 'ANNUALLY'];
const emptyLine = () => ({ account: '', debit: '', credit: '', memo: '' });
const emptyForm = () => ({ entity: '', name: '', currency: '', frequency: 'MONTHLY', memo_template: '', next_run_date: '', lines: [emptyLine(), emptyLine()] });

const RecurringTemplates = () => {
  const { isFinanceLeadership } = useAuth();
  const [entities, setEntities] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [entRes, curRes, accRes, tplRes] = await Promise.all([
        financeAPI.entities.list(), financeAPI.currencies.list(), financeAPI.accounts.list(), financeAPI.recurringTemplates.list(),
      ]);
      setEntities(entRes.data.results || entRes.data || []);
      setCurrencies(curRes.data.results || curRes.data || []);
      setAccounts(accRes.data.results || accRes.data || []);
      setTemplates(tplRes.data.results || tplRes.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || 'Failed to load recurring templates.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateLine = (idx, field, value) => {
    setForm((prev) => { const lines = [...prev.lines]; lines[idx] = { ...lines[idx], [field]: value }; return { ...prev, lines }; });
  };
  const addLine = () => setForm((prev) => ({ ...prev, lines: [...prev.lines, emptyLine()] }));
  const removeLine = (idx) => setForm((prev) => ({ ...prev, lines: prev.lines.filter((_, i) => i !== idx) }));

  const submitForm = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = { ...form, lines: form.lines.filter((l) => l.account && (l.debit || l.credit)) };
      await financeAPI.recurringTemplates.create(payload);
      setForm(emptyForm());
      setShowForm(false);
      load();
    } catch (e2) {
      setError(e2?.response?.data?.error || JSON.stringify(e2?.response?.data) || 'Failed to create recurring template.');
    } finally {
      setSaving(false);
    }
  };

  const generateNow = async (tpl) => {
    if (!window.confirm(`Generate a draft journal entry from "${tpl.name}" now?`)) return;
    setBusyId(tpl.id);
    setError(null);
    try {
      await financeAPI.recurringTemplates.generateNow(tpl.id);
      window.alert('Draft journal entry created — review it on the Journal Entries page.');
      load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to generate entry.');
    } finally {
      setBusyId(null);
    }
  };

  const entityName = (id) => entities.find((e) => e.id === id)?.name || `#${id}`;
  const accountLabel = (id) => { const a = accounts.find((x) => x.id === id); return a ? `${a.code} — ${a.name}` : `#${id}`; };
  const accountsFor = (entityId) => accounts.filter((a) => !entityId || String(a.entity) === String(entityId));

  return (
    <div style={fs.page}>
      <div style={fs.pageHeader}>
        <div>
          <h1 style={fs.pageTitle}><FiRepeat size={22} />Recurring Templates</h1>
          <p style={fs.pageSubtitle}>Recurring journal entry templates. A DRAFT entry is generated automatically on schedule (or on demand below) — it still needs to be reviewed and posted.</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setShowForm((v) => !v)} style={fs.primaryButton}><FiPlus size={14} />New Template</button>
          <button onClick={load} title="Refresh" style={fs.iconButton}><FiRefreshCw size={15} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /></button>
        </div>
      </div>

      {error && (
        <div style={fs.errorBanner}>
          <FiAlertTriangle size={18} /><span>{error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: fs.colors.red, cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      {showForm && (
        <form onSubmit={submitForm} style={{ ...fs.card, marginBottom: 22 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 16 }}>
            <div><div style={fs.label}>Entity</div>
              <select required value={form.entity} onChange={(e) => setForm({ ...form, entity: e.target.value })} style={{ ...fs.select, width: '100%' }}>
                <option value="">Select…</option>{entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div><div style={fs.label}>Name</div><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={{ ...fs.input, width: '100%' }} /></div>
            <div><div style={fs.label}>Currency</div>
              <select required value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} style={{ ...fs.select, width: '100%' }}>
                <option value="">Select…</option>{currencies.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
              </select>
            </div>
            <div><div style={fs.label}>Frequency</div>
              <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} style={{ ...fs.select, width: '100%' }}>
                {FREQUENCIES.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div><div style={fs.label}>Next Run Date</div><input required type="date" value={form.next_run_date} onChange={(e) => setForm({ ...form, next_run_date: e.target.value })} style={{ ...fs.input, width: '100%' }} /></div>
            <div style={{ gridColumn: 'span 2' }}><div style={fs.label}>Memo Template</div><input required value={form.memo_template} onChange={(e) => setForm({ ...form, memo_template: e.target.value })} style={{ ...fs.input, width: '100%' }} /></div>
          </div>

          <div style={fs.label}>Lines (must balance — total debits = total credits)</div>
          {form.lines.map((line, idx) => (
            <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <select value={line.account} onChange={(e) => updateLine(idx, 'account', e.target.value)} style={{ ...fs.select, flex: 2 }}>
                <option value="">Account…</option>{accountsFor(form.entity).map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
              </select>
              <input type="number" step="0.01" placeholder="Debit" value={line.debit} onChange={(e) => updateLine(idx, 'debit', e.target.value)} style={{ ...fs.input, width: 110 }} />
              <input type="number" step="0.01" placeholder="Credit" value={line.credit} onChange={(e) => updateLine(idx, 'credit', e.target.value)} style={{ ...fs.input, width: 110 }} />
              <input placeholder="Memo" value={line.memo} onChange={(e) => updateLine(idx, 'memo', e.target.value)} style={{ ...fs.input, flex: 1 }} />
              <button type="button" onClick={() => removeLine(idx)} style={{ ...fs.iconButton, padding: 8 }}><FiTrash2 size={13} /></button>
            </div>
          ))}
          <button type="button" onClick={addLine} style={{ ...fs.neutralButton, marginBottom: 16 }}><FiPlus size={12} style={{ marginRight: 4, verticalAlign: -1 }} />Add Line</button>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" disabled={saving} style={fs.primaryButton}>{saving ? 'Saving…' : 'Create Template'}</button>
          </div>
        </form>
      )}

      {loading ? (
        <div style={fs.loadingState}>Loading…</div>
      ) : templates.length === 0 ? (
        <div style={fs.emptyState}><FiRepeat size={44} style={{ marginBottom: 16 }} /><div style={{ fontSize: 15, color: fs.colors.textGhost }}>No recurring templates yet.</div></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {templates.map((tpl) => {
            const busy = busyId === tpl.id;
            return (
              <div key={tpl.id} style={fs.card}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ color: fs.colors.textBright, fontWeight: 600, fontSize: 14 }}>{tpl.name}</span>
                      <span style={fs.badge(tpl.is_balanced ? fs.colors.green : fs.colors.amber)}>{tpl.is_balanced ? 'BALANCED' : 'UNBALANCED'}</span>
                      {!tpl.is_active && <span style={fs.badge('#888')}>INACTIVE</span>}
                    </div>
                    <div style={{ color: fs.colors.textFaint, fontSize: 12, marginTop: 4 }}>
                      {entityName(tpl.entity)} · {tpl.frequency} · next run {tpl.next_run_date}
                      {tpl.last_generated_at && ` · last generated ${new Date(tpl.last_generated_at).toLocaleDateString()}`}
                    </div>
                    <div style={{ color: fs.colors.textDim, fontSize: 12, marginTop: 6 }}>
                      {(tpl.lines || []).map((l) => `${accountLabel(l.account)} ${l.debit && parseFloat(l.debit) > 0 ? `DR ${l.debit}` : `CR ${l.credit}`}`).join(' · ')}
                    </div>
                  </div>
                  {isFinanceLeadership() && (
                    <button onClick={() => generateNow(tpl)} disabled={busy || !tpl.is_balanced} style={fs.successButton}><FiPlay size={12} style={{ marginRight: 4, verticalAlign: -1 }} />Generate Now</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RecurringTemplates;
